/**
 * Geolocation-based currency detection.
 *
 * Rules:
 *  - European countries  → EUR
 *  - Canada              → CAD
 *  - Everywhere else     → USD
 *
 * If a detected currency is not available in a plan's prices array,
 * the caller should fall back to EUR (handled by the consuming components).
 */

// European countries (EU + EEA + other EUR-zone)
const EUROPEAN_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA + nearby
  'NO', 'IS', 'LI', 'CH', 'GB', 'MC', 'AD', 'SM', 'VA', 'ME',
  'RS', 'BA', 'MK', 'AL', 'MD', 'UA', 'BY', 'XK',
]);

/**
 * Map a country code to the appropriate currency.
 */
const countryToCurrency = (countryCode) => {
  if (!countryCode) return 'EUR';
  const code = countryCode.toUpperCase();
  if (EUROPEAN_COUNTRIES.has(code)) return 'EUR';
  if (code === 'CA') return 'CAD';
  return 'USD';
};

// In-memory cache so we only call the API once per session
let cachedCurrency = null;

/**
 * Detect the user's currency based on their IP geolocation.
 * Uses ipapi.co (free tier, HTTPS, no key required for low volume).
 * Caches the result for the session.
 *
 * @returns {Promise<string>} Currency code: 'EUR', 'CAD', or 'USD'
 */
export const detectCurrency = async () => {
  if (cachedCurrency) return cachedCurrency;

  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) throw new Error('Geolocation API error');
    const data = await response.json();
    cachedCurrency = countryToCurrency(data.country_code);
  } catch (error) {
    console.warn('Geolocation detection failed, defaulting to EUR:', error);
    cachedCurrency = 'EUR';
  }

  return cachedCurrency;
};

/**
 * Get the symbol for a currency code.
 */
export const getCurrencySymbol = (currency) => {
  const symbols = { EUR: '€', USD: '$', CAD: 'C$', GBP: '£' };
  return symbols[currency] || currency;
};

/**
 * Get a plan's price for a given currency.
 * Falls back to EUR, then to the first available price, then to the legacy `plan.price` field.
 */
export const getPlanPrice = (plan, currency = 'EUR') => {
  if (plan?.prices && Array.isArray(plan.prices)) {
    const match = plan.prices.find(p => p.currency === currency);
    if (match) return { price: match.price, currency: match.currency };

    // Fallback to EUR
    const eurFallback = plan.prices.find(p => p.currency === 'EUR');
    if (eurFallback) return { price: eurFallback.price, currency: 'EUR' };

    // Fallback to first available
    if (plan.prices.length > 0) return { price: plan.prices[0].price, currency: plan.prices[0].currency };
  }

  // Legacy fallback
  return { price: plan?.price || 0, currency: 'EUR' };
};
